import React from 'react';
import { motion } from 'framer-motion';
import { useFinancial } from '../context/FinancialContext';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ReactECharts from 'echarts-for-react';

const { FiBarChart3, FiTarget, FiTrendingUp, FiCalendar } = FiIcons;

function Dashboard() {
  const { state } = useFinancial();

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      minimumFractionDigits: 0
    }).format(value);
  };

  // Calculate timeline data
  const timelineData = React.useMemo(() => {
    const currentYear = new Date().getFullYear();
    const timelineMap = new Map();
    
    state.goals.forEach(goal => {
      const targetYear = currentYear + goal.timeHorizon;
      if (!timelineMap.has(targetYear)) {
        timelineMap.set(targetYear, []);
      }
      timelineMap.get(targetYear).push(goal.name);
    });

    return Array.from(timelineMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([year, goals]) => ({ year, goals }));
  }, [state.goals]);

  // Calculate cumulative investment data
  const investmentGrowthData = React.useMemo(() => {
    const years = Array.from({ length: 30 }, (_, i) => i + 1);
    let cumulativeGoalNeeds = 0;
    let cumulativeInvestments = 0;

    return years.map(year => {
      // Add goals that mature this year
      state.goals.forEach(goal => {
        if (goal.timeHorizon === year) {
          cumulativeGoalNeeds += goal.futureValue || 0;
        }
      });

      // Calculate cumulative investments (simplified)
      const annualSIP = state.goals.reduce((sum, goal) => sum + (goal.requiredSIP || 0), 0) * 12;
      cumulativeInvestments += annualSIP;

      return {
        year,
        goalNeeds: cumulativeGoalNeeds,
        investments: cumulativeInvestments
      };
    });
  }, [state.goals]);

  const timelineChartOption = {
    title: {
      text: 'Goals Timeline',
      left: 'center',
      textStyle: { fontSize: 16, fontWeight: 'normal' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        const data = timelineData[params[0].dataIndex];
        return `${data.year}: ${data.goals.join(', ')}`;
      }
    },
    xAxis: {
      type: 'category',
      data: timelineData.map(item => item.year),
      name: 'Year',
      nameLocation: 'middle',
      nameGap: 25
    },
    yAxis: {
      type: 'value',
      name: 'Number of Goals',
      nameLocation: 'middle',
      nameGap: 40
    },
    series: [{
      data: timelineData.map(item => item.goals.length),
      type: 'bar',
      itemStyle: { color: '#19b48d' },
      barWidth: '60%'
    }],
    grid: {
      left: '10%',
      right: '5%',
      top: '15%',
      bottom: '15%'
    }
  };

  const investmentGrowthOption = {
    title: {
      text: 'Cumulative Investment vs Goal Needs',
      left: 'center',
      textStyle: { fontSize: 16, fontWeight: 'normal' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        return `Year ${params[0].axisValue}: Investments ${formatCurrency(params[0].value)}, Goals ${formatCurrency(params[1].value)}`;
      }
    },
    legend: {
      data: ['Investments', 'Goal Needs'],
      top: 25
    },
    xAxis: {
      type: 'category',
      data: investmentGrowthData.map(item => item.year),
      name: 'Years from Now',
      nameLocation: 'middle',
      nameGap: 25
    },
    yAxis: {
      type: 'value',
      name: 'Amount (₹)',
      nameLocation: 'middle',
      nameGap: 50,
      axisLabel: {
        formatter: (value) => `₹${(value / 10000000).toFixed(1)}Cr`
      }
    },
    series: [
      {
        name: 'Investments',
        type: 'line',
        data: investmentGrowthData.map(item => item.investments),
        itemStyle: { color: '#19b48d' },
        areaStyle: { color: 'rgba(25, 180, 141, 0.1)' }
      },
      {
        name: 'Goal Needs',
        type: 'line',
        data: investmentGrowthData.map(item => item.goalNeeds),
        itemStyle: { color: '#f58320' },
        areaStyle: { color: 'rgba(245, 131, 32, 0.1)' }
      }
    ],
    grid: {
      left: '12%',
      right: '5%',
      top: '15%',
      bottom: '15%'
    }
  };

  const sipVsLumpsumData = state.goals.map(goal => ({
    name: goal.name,
    sip: goal.requiredSIP * 12 * goal.timeHorizon || 0,
    lumpsum: goal.requiredLumpSum || 0
  }));

  const sipVsLumpsumOption = {
    title: {
      text: 'SIP vs Lumpsum by Goal',
      left: 'center',
      textStyle: { fontSize: 16, fontWeight: 'normal' }
    },
    tooltip: {
      trigger: 'axis',
      formatter: function(params) {
        return `${params[0].axisValue}: SIP ${formatCurrency(params[0].value)}, Lumpsum ${formatCurrency(params[1].value)}`;
      }
    },
    legend: {
      data: ['Total SIP', 'Lumpsum'],
      top: 25
    },
    xAxis: {
      type: 'category',
      data: sipVsLumpsumData.map(item => item.name),
      axisLabel: {
        rotate: 45,
        fontSize: 10
      }
    },
    yAxis: {
      type: 'value',
      name: 'Amount (₹)',
      nameLocation: 'middle',
      nameGap: 50,
      axisLabel: {
        formatter: (value) => `₹${(value / 100000).toFixed(0)}L`
      }
    },
    series: [
      {
        name: 'Total SIP',
        type: 'bar',
        data: sipVsLumpsumData.map(item => item.sip),
        itemStyle: { color: '#19b48d' }
      },
      {
        name: 'Lumpsum',
        type: 'bar',
        data: sipVsLumpsumData.map(item => item.lumpsum),
        itemStyle: { color: '#f58320' }
      }
    ],
    grid: {
      left: '12%',
      right: '5%',
      top: '15%',
      bottom: '25%'
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="max-w-7xl mx-auto"
    >
      <div className="flex items-center space-x-3 mb-8">
        <div className="w-10 h-10 rounded-lg bg-[#19b48d] flex items-center justify-center">
          <SafeIcon icon={FiBarChart3} className="text-white text-lg" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Financial Dashboard</h2>
          <p className="text-gray-600">Visual insights into your financial planning progress</p>
        </div>
      </div>

      {/* Key Metrics */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#19b48d]/10 flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="text-[#19b48d] text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Active Goals</p>
              <p className="text-2xl font-bold text-gray-900">{state.goals.length}</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-[#f58320]/10 flex items-center justify-center">
              <SafeIcon icon={FiTrendingUp} className="text-[#f58320] text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Total Monthly SIP</p>
              <p className="text-2xl font-bold text-gray-900">
                {formatCurrency(state.goals.reduce((sum, goal) => sum + (goal.requiredSIP || 0), 0))}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <SafeIcon icon={FiCalendar} className="text-blue-600 text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Avg. Time Horizon</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.goals.length > 0 
                  ? Math.round(state.goals.reduce((sum, goal) => sum + goal.timeHorizon, 0) / state.goals.length)
                  : 0} years
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
              <SafeIcon icon={FiTarget} className="text-green-600 text-sm" />
            </div>
            <div>
              <p className="text-sm text-gray-600">Goals on Track</p>
              <p className="text-2xl font-bold text-gray-900">
                {state.goals.filter(goal => goal.achievementProbability >= 90).length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-80">
            <ReactECharts option={timelineChartOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>

        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
          <div className="h-80">
            <ReactECharts option={sipVsLumpsumOption} style={{ height: '100%', width: '100%' }} />
          </div>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
        <div className="h-96">
          <ReactECharts option={investmentGrowthOption} style={{ height: '100%', width: '100%' }} />
        </div>
      </div>

      {/* Timeline Table */}
      {timelineData.length > 0 && (
        <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mt-8">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Goals Timeline</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-200">
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Year</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Goals</th>
                  <th className="text-left py-3 px-4 font-medium text-gray-700">Count</th>
                </tr>
              </thead>
              <tbody>
                {timelineData.map(({ year, goals }) => (
                  <tr key={year} className="border-b border-gray-100">
                    <td className="py-3 px-4 font-medium text-gray-900">{year}</td>
                    <td className="py-3 px-4 text-gray-600">{goals.join(', ')}</td>
                    <td className="py-3 px-4 text-gray-900">{goals.length}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </motion.div>
  );
}

export default Dashboard;